import { useEffect, useState } from 'react';

import { fetchIdeaBatches, ideaBatchPath, type IdeaBatchSummary } from '../api.ts';
import { DASHBOARD_POLL_MS, describeError, redirectIfUnauthorised, useKeyChords } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { Link, navigate } from '../router.tsx';
import { since } from '../schedule.ts';
import { Badge, EmptyState, PageHeader, Skeleton } from '../ui.tsx';
import * as wording from '../wording.ts';

/**
 * Every idea batch, newest first (US-122).
 *
 * The list is not part of the app-wide poll in `data.tsx`: it only moves while
 * a batch is being worked, so it re-reads on the dashboard's beat while one is
 * waiting or working and sits still otherwise.
 */
export function Ideas() {
  const [batches, setBatches] = useState<IdeaBatchSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useKeyChords({ n: () => navigate('/ideas/new') });

  const moving = (batches ?? []).some(
    (batch) => batch.status === 'queued' || batch.status === 'working',
  );

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchIdeaBatches(controller.signal)
        .then((loaded) => {
          setBatches(loaded);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted) return;
          if (redirectIfUnauthorised(cause)) return;
          setError(describeError(cause));
        });
    };
    load();
    // Only while something is moving; the effect runs again when that changes.
    const timer = moving
      ? window.setInterval(() => {
          if (document.visibilityState === 'visible') load();
        }, DASHBOARD_POLL_MS)
      : null;
    return () => {
      controller.abort();
      if (timer !== null) window.clearInterval(timer);
    };
  }, [moving]);

  const newBatch = (
    <Link className="button button--primary" href="/ideas/new" title="n">
      <Icon name="plus" />
      {wording.NEW_BATCH}
    </Link>
  );

  return (
    <div className="page">
      <PageHeader title={wording.IDEAS} subtitle={wording.IDEAS_SUBTITLE} actions={newBatch} />

      {error !== null && (
        <div className="notice notice--error" role="alert">
          <Icon name="x-circle" />
          <div className="notice__body">
            {wording.IDEAS_READ_FAILED} {error}
          </div>
        </div>
      )}

      {batches === null ? (
        <div className="panel">
          <div className="panel__body">
            <Skeleton lines={4} />
          </div>
        </div>
      ) : batches.length === 0 ? (
        <EmptyState
          icon="bulb"
          title={wording.NO_BATCHES}
          action={
            <Link className="button button--primary" href="/ideas/new">
              <Icon name="plus" />
              {wording.NEW_BATCH}
            </Link>
          }
        >
          {wording.NO_BATCHES_BODY}
        </EmptyState>
      ) : (
        <div className="table-wrap table-wrap--cards panel">
          <table className="table table--ideas table--cards">
            <thead>
              <tr>
                <th>{wording.BATCH_COLUMN}</th>
                <th>{wording.BATCH_REPOSITORY}</th>
                <th>{wording.BATCH_IDEAS}</th>
                <th>{wording.BATCH_PROGRESS}</th>
                <th className="num">{wording.BATCH_COST}</th>
                <th>{wording.BATCH_CREATED}</th>
                <th className="table__actions">
                  <span className="visually-hidden">{wording.OPEN}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <BatchRow key={batch.id} batch={batch} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BatchRow({ batch }: { readonly batch: IdeaBatchSummary }) {
  const href = ideaBatchPath(batch.id);
  const { counts } = batch;
  // A batch suggesting its own ideas has none yet; the number asked for is
  // what it is about to hold.
  const total = counts.total === 0 && batch.requestedCount !== null ? batch.requestedCount : counts.total;
  return (
    <tr className={batch.status === 'failed' ? 'table__row--attention' : undefined}>
      <td className="table__cell--lead">
        <div className="cell-stack">
          <span className="cell-stack__title">
            <Link className="link link--strong" href={href}>
              {batch.title}
            </Link>
            <BatchBadge batch={batch} />
          </span>
          {batch.status === 'failed' && batch.error !== null && (
            <span className="cell-stack__meta">{batch.error}</span>
          )}
        </div>
      </td>
      <td
        className="table__tags"
        data-label={wording.BATCH_REPOSITORY}
        title={batch.primaryRepository?.name ?? undefined}
      >
        {batch.primaryRepository?.name ?? <span className="muted">{wording.NO_REPOSITORY}</span>}
      </td>
      <td className="table__phrase" data-label={wording.BATCH_IDEAS}>{wording.ideaCount(total)}</td>
      <td className="table__phrase" data-label={wording.BATCH_PROGRESS}>{progress(batch)}</td>
      <td className="num table__phrase" data-label={wording.BATCH_COST}>
        {wording.dollars(batch.costUsd)}
      </td>
      <td className="table__time" data-label={wording.BATCH_CREATED} title={batch.createdAt}>
        {since(batch.createdAt)}
      </td>
      <td className="table__actions table__cell--foot">
        <Link className="button button--small button--quiet" href={href}>
          {wording.OPEN}
          <Icon name="chevron-right" />
        </Link>
      </td>
    </tr>
  );
}

function BatchBadge({ batch }: { readonly batch: IdeaBatchSummary }) {
  const label = wording.BATCH_STATUS[batch.status];
  switch (batch.status) {
    case 'queued':
      return <Badge tone="neutral">{label}</Badge>;
    case 'working':
      return (
        <Badge tone="active" pulse>
          {label}
        </Badge>
      );
    case 'done':
      return <Badge tone="done">{label}</Badge>;
    case 'failed':
      return <Badge tone="danger">{label}</Badge>;
  }
}

/**
 * How far the batch has got: worked out while it is working, what is left to
 * decide once it is done, and "done" when nothing is.
 */
function progress(batch: IdeaBatchSummary): string {
  const { counts } = batch;
  if (batch.status === 'queued') return wording.BATCH_STATUS.queued;
  if (batch.status === 'working') {
    if (counts.total === 0) return wording.IDEAS_SUGGESTING;
    return wording.ideasWorked(counts.total - counts.left, counts.total);
  }
  if (counts.drafted > 0) return wording.ideasToDecide(counts.drafted);
  if (counts.failed > 0) return wording.ideasNotWorked(counts.failed);
  // A batch that stopped as a whole says so in its badge; here, how far it got.
  if (batch.status === 'failed') {
    return counts.total === 0
      ? wording.NOTHING_WORKED_OUT
      : wording.ideasWorked(counts.total - counts.left, counts.total);
  }
  return wording.IDEAS_ALL_DECIDED;
}
